import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, FormControl, Alert, Skeleton, Dialog, DialogTitle,
  DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, Divider,
  Autocomplete,
} from '@mui/material';
import {
  Send as SendIcon,
  AppRegistration as RegisterIcon,
  Search as SearchIcon,
  SendToMobile as MobileIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { schoolPayAPI, studentAPI } from '../../services/api';
import AmountField from '../common/AmountField';

const SchoolPayAdhocPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState({
    amount: '',
    external_reference: '',
    phone_number: '',
    reason: '',
  });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearchOptions, setStudentSearchOptions] = useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);
  const [copiedRef, setCopiedRef] = useState(false);
  const [checkRef, setCheckRef] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const resetForm = () => {
    setForm({ amount: '', external_reference: '', phone_number: '', reason: '' });
    setSelectedStudent(null);
    setStudentSearchOptions([]);
    setStudentSearchInput('');
    setSuccessInfo(null);
  };

  const handleCopyReference = (reference) => {
    navigator.clipboard.writeText(reference);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 2000);
  };

  const searchStudents = async (searchTerm) => {
    setStudentSearchInput(searchTerm);

    if (!searchTerm || searchTerm.length < 2) {
      setStudentSearchOptions([]);
      return;
    }

    setStudentSearchLoading(true);
    try {
      const response = await studentAPI.search({ search: searchTerm });
      const students = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setStudentSearchOptions(students);
    } catch (err) {
      setStudentSearchOptions([]);
    } finally {
      setStudentSearchLoading(false);
    }
  };

  const generateReference = (student) => {
    const stamp = new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14); // YYYYMMDDHHmmss
    return `ADHOC-${student.registration_no}-${stamp}`;
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setForm(prev => ({ ...prev, external_reference: student ? generateReference(student) : '' }));
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const { data } = await schoolPayAPI.getAdhocPayments();
      setPayments(data.data || []);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedStudent) {
      setMessage({ type: 'error', text: 'Please select a student.' });
      return;
    }

    setSubmitting(true);
    setMessage(null);
    try {
      const payload = { ...form, student_id: selectedStudent.id };
      const apiCall = mode === 'register' ? schoolPayAPI.adhocRegister : schoolPayAPI.adhocRequest;
      const { data } = await apiCall(payload);
      if (data.success) {
        setSuccessInfo({
          mode,
          reference: data.data?.paymentReference || '',
          payerName: `${selectedStudent.first_name} ${selectedStudent.last_name}`,
          phoneNumber: form.phone_number,
        });
        fetchPayments();
      } else {
        setMessage({ type: 'error', text: data.data?.returnMessage || 'Request failed.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Request failed.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!checkRef) return;
    setChecking(true);
    setCheckResult(null);
    try {
      const { data } = await schoolPayAPI.adhocCheck(checkRef);
      setCheckResult(data.data);
      fetchPayments();
    } catch (err) {
      setCheckResult({ error: 'Failed to check status.' });
    } finally {
      setChecking(false);
    }
  };

  const getStatusChip = (status) => {
    const config = {
      PAID: { color: 'success' },
      PENDING: { color: 'warning' },
      FAILED: { color: 'error' },
      EXPIRED: { color: 'default' },
    };
    const c = config[status] || { color: 'default' };
    return <Chip label={status} color={c.color} size="small" variant="outlined" />;
  };

  if (loading) {
    return (
      <Box>
        <Box mb={4}>
          <Skeleton variant="text" width={200} height={32} />
          <Skeleton variant="text" width={350} height={20} />
        </Box>
        <Card>
          <CardContent>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="text" height={40} sx={{ mb: 1 }} />
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4" gutterBottom>Payment Requests</Typography>
            <Typography variant="body1" color="text.secondary">
              Send a mobile money prompt to a payer's phone, or generate a payment reference they can pay later
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => setShowForm(true)}>
              New Payment
            </Button>
          </Grid>
        </Grid>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Status Check */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon fontSize="small" /> Check Payment Status
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item size={{ lg: 5, md: 5, sm: 12, xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Payment Reference"
                value={checkRef}
                onChange={e => setCheckRef(e.target.value)}
                placeholder="e.g., 21AD100010"
                helperText="Enter the SchoolPay payment reference to check"
              />
            </Grid>
            <Grid item size={{ lg: 2, md: 2, sm: 12, xs: 12 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCheckStatus}
                disabled={!checkRef || checking}
                startIcon={<SearchIcon />}
                size="medium"
              >
                {checking ? 'Checking...' : 'Check'}
              </Button>
            </Grid>
            {checkResult && (
              <Grid item size={{ lg: 5, md: 5, sm: 12, xs: 12 }}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  {checkResult.error ? (
                    <Typography variant="body2" color="error">{checkResult.error}</Typography>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item size={{ lg: 4, md: 4, sm: 4, xs: 4 }}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box mt={0.5}>{getStatusChip(checkResult.status)}</Box>
                      </Grid>
                      {checkResult.receiptNumber && (
                        <Grid item size={{ lg: 4, md: 4, sm: 4, xs: 4 }}>
                          <Typography variant="caption" color="text.secondary">Receipt</Typography>
                          <Typography variant="body2" fontWeight={500}>{checkResult.receiptNumber}</Typography>
                        </Grid>
                      )}
                      {checkResult.transactionId && (
                        <Grid item size={{ lg: 4, md: 4, sm: 4, xs: 4 }}>
                          <Typography variant="caption" color="text.secondary">Transaction ID</Typography>
                          <Typography variant="body2" fontWeight={500}>{checkResult.transactionId}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Request History</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Payer</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ledger</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No payment requests yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {p.payment_reference || p.external_reference}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {p.first_name} {p.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          UGX {Number(p.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{p.phone_number || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{p.reason || '-'}</Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(p.status)}</TableCell>
                      <TableCell>
                        {p.status === 'PAID' ? (
                          p.fee_payment_id ? (
                            <Chip label="Credited" color="success" size="small" variant="outlined" />
                          ) : (
                            <Chip label="Not Credited" color="warning" size="small" variant="outlined" />
                          )
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(p.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* New Payment Dialog */}
      <Dialog open={showForm} onClose={() => { setShowForm(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {successInfo
            ? (successInfo.mode === 'register' ? 'Reference Generated' : 'Payment Prompt Sent')
            : (mode === 'register' ? 'Generate Payment Reference' : 'Push Payment to Phone')}
        </DialogTitle>
        <DialogContent>
          {successInfo ? (
            <Box sx={{ pt: 1 }}>
              <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
                {successInfo.mode === 'register'
                  ? `A payment reference has been generated for ${successInfo.payerName}.`
                  : `A mobile money payment prompt was sent to ${successInfo.phoneNumber} for ${successInfo.payerName}.`}
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary">Payment Reference</Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', flex: 1 }}>
                    {successInfo.reference}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={copiedRef ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    onClick={() => handleCopyReference(successInfo.reference)}
                  >
                    {copiedRef ? 'Copied' : 'Copy'}
                  </Button>
                </Box>
              </Paper>

              <Typography variant="body2" color="text.secondary">
                {successInfo.mode === 'register'
                  ? `Give this reference to ${successInfo.payerName} — they can complete the payment via mobile money, bank, or a SchoolPay agent by quoting this code. Nothing is charged until they do.`
                  : `Ask ${successInfo.payerName} to check their phone and enter their Mobile Money PIN to approve. Use "Check Payment Status" below once they've confirmed.`}
              </Typography>
            </Box>
          ) : (
            <>
          <Box sx={{ mb: 2, mt: 1 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(e, val) => { if (val) setMode(val); }}
              size="small"
              fullWidth
            >
              <ToggleButton value="register">
                <RegisterIcon sx={{ mr: 0.5, fontSize: 16 }} /> Generate Reference (Payer Pays Later)
              </ToggleButton>
              <ToggleButton value="request">
                <MobileIcon sx={{ mr: 0.5, fontSize: 16 }} /> Push to Phone (Pay Now)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Autocomplete
              fullWidth
              options={Array.isArray(studentSearchOptions) ? studentSearchOptions : []}
              value={selectedStudent}
              inputValue={studentSearchInput}
              onChange={(event, newValue) => handleStudentSelect(newValue)}
              onInputChange={(event, newInputValue) => searchStudents(newInputValue)}
              loading={studentSearchLoading}
              getOptionLabel={(option) => option ? `${option.registration_no} - ${option.full_name}` : ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {option.registration_no}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.full_name} ({option.class_stream_name})
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Student"
                  placeholder="Type name or registration number..."
                  required
                  margin="normal"
                  helperText="Payments must be linked to a student to be credited to their fee balance"
                />
              )}
              noOptionsText={studentSearchInput.length < 2 ? "Type at least 2 characters" : "No students found"}
              loadingText="Searching..."
              filterOptions={(x) => x}
            />
            <AmountField
              label="Amount (UGX)"
              value={form.amount}
              onChange={(val) => setForm(prev => ({ ...prev, amount: val }))}
              required
            />
            <TextField
              fullWidth
              label="Your Reference"
              value={form.external_reference}
              onChange={e => setForm(prev => ({ ...prev, external_reference: e.target.value }))}
              required
              margin="normal"
              placeholder="e.g., INV-001"
              helperText="Auto-generated when you select a student — override if you'd like to use your own invoice/receipt number"
            />
            <Grid container spacing={3}>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={selectedStudent?.first_name || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={selectedStudent?.last_name || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
            {mode === 'request' && (
              <TextField
                fullWidth
                label="Phone Number"
                value={form.phone_number}
                onChange={e => setForm(prev => ({ ...prev, phone_number: e.target.value }))}
                required
                margin="normal"
                placeholder="0771000955"
                helperText="The payer will receive a debit prompt on this number"
              />
            )}
            <TextField
              fullWidth
              label="Reason"
              value={form.reason}
              onChange={e => setForm(prev => ({ ...prev, reason: e.target.value }))}
              margin="normal"
              placeholder="e.g., Admissions 2025"
            />
          </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {successInfo ? (
            <Button onClick={() => { setShowForm(false); resetForm(); }} variant="contained">Done</Button>
          ) : (
            <>
              <Button onClick={() => { setShowForm(false); resetForm(); }} disabled={submitting}>Cancel</Button>
              <Button onClick={handleSubmit} variant="contained" disabled={submitting || !selectedStudent} startIcon={<SendIcon />}>
                {submitting ? 'Processing...' : (mode === 'register' ? 'Generate Reference' : 'Send Request')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolPayAdhocPayments;
